import { Dialog } from "@ark-ui/react/dialog";
import { Portal } from "@ark-ui/react/portal";
import { Icon } from "@ttc3k/trekker";
import { Xmark } from "iconoir-react";
import { useEffect, type CSSProperties, type PropsWithChildren } from "react";
import { css, cx } from "styled-system/css";
import { styled } from "styled-system/jsx";
import { SLIDEOUT_DIALOG_Z_INDEX } from "./slideoutDialog.constants";
import { clearStaleBodyOverlayStyles } from "./slideoutDialog.utils";

type SlideoutPanePlacement =
  | "rightPane"
  | "rightLargePane"
  | "leftPane"
  | "leftLargePane";

export type SlideoutDialogProps = PropsWithChildren & {
  open?: boolean;
  onOpenChange?: (details: { open: boolean }) => void;
  placement?: SlideoutPanePlacement;
  portal?: boolean;
  hideCloseTrigger?: boolean;
  closeTriggerVisual?: "light" | "subtle" | "dark";
  zIndex?: number;
};

const layerClass = css({
  zIndex: "max",
});

const backdropClass = css({
  bg: "ink.800/70",
  position: "fixed",
  inset: 0,
  _open: {
    animation: "fadeIn 200ms",
    animationTimingFunction: "entrance.productive",
  },
  _closed: {
    animation: "fadeOut 200ms",
    animationTimingFunction: "exit.productive",
  },
});

const positionerClass = css({
  position: "fixed",
  inset: 0,
  display: "flex",
  overflow: "hidden",
});

const placementPositionerClass: Record<SlideoutPanePlacement, string> = {
  rightPane: css({ justifyContent: "flex-end" }),
  rightLargePane: css({ justifyContent: "flex-end" }),
  leftPane: css({ justifyContent: "flex-start" }),
  leftLargePane: css({ justifyContent: "flex-start" }),
};

const contentBaseClass = css({
  bg: "white",
  position: "relative",
  outline: "none",
  h: "100dvh",
  maxH: "100dvh",
  minH: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  _open: {
    animation: "rightToOrigin 300ms",
    animationTimingFunction: "entrance.expressive",
  },
  _closed: {
    animation: "originToRight 300ms",
    animationTimingFunction: "exit.expressive",
  },
  mdDown: {
    borderTopLeftRadius: "300",
    borderTopRightRadius: "300",
    mt: "300",
    alignSelf: "flex-end",
    w: "full",
  },
});

const placementContentClass: Record<SlideoutPanePlacement, string> = {
  rightPane: css({
    md: {
      width: "modals.pane",
      boxShadow: "-25px 0px 40px -15px rgba(10, 10, 10, 0.15)",
    },
  }),
  rightLargePane: css({
    md: {
      width: "modals.largePane",
      boxShadow: "-25px 0px 40px -15px rgba(10, 10, 10, 0.15)",
    },
  }),
  leftPane: css({
    md: {
      width: "modals.pane",
      boxShadow: "25px 0px 40px -15px rgba(10, 10, 10, 0.15)",
    },
    _open: {
      animation: "leftToOrigin 300ms",
      animationTimingFunction: "entrance.expressive",
    },
    _closed: {
      animation: "originToLeft 300ms",
      animationTimingFunction: "exit.expressive",
    },
  }),
  leftLargePane: css({
    md: {
      width: "modals.largePane",
      boxShadow: "25px 0px 40px -15px rgba(10, 10, 10, 0.15)",
    },
    _open: {
      animation: "leftToOrigin 300ms",
      animationTimingFunction: "entrance.expressive",
    },
    _closed: {
      animation: "originToLeft 300ms",
      animationTimingFunction: "exit.expressive",
    },
  }),
};

const closeTriggerBaseClass = css({
  position: "absolute",
  top: "400",
  right: "250",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  w: "32px",
  h: "32px",
  borderRadius: "full",
  cursor: "pointer",
  border: "none",
  outline: "none",
  zIndex: 1,
});

const closeTriggerVisualClass: Record<
  NonNullable<SlideoutDialogProps["closeTriggerVisual"]>,
  string
> = {
  light: css({
    color: "white",
    bg: "bg.darkest/75",
    backdropFilter: "blur(2px)",
    _hover: { bg: "bg.darkest/95" },
    _focusVisible: { bg: "bg.darkest/95" },
  }),
  subtle: css({
    color: "text.light",
    bg: "transparent",
    _hover: { color: "text.mid" },
  }),
  dark: css({
    color: "text.dark",
    bg: "transparent",
    _hover: { color: "text.mid" },
  }),
};

/**
 * Right/left pane dialog without trekker Modal's `#trekker-scroll-container`
 * wrapper, which breaks sticky slideout footers in Chrome when form controls
 * update layout.
 */
export const SlideoutDialog = ({
  open,
  onOpenChange,
  children,
  placement = "rightLargePane",
  portal = true,
  hideCloseTrigger = false,
  closeTriggerVisual = "light",
  zIndex = SLIDEOUT_DIALOG_Z_INDEX,
}: SlideoutDialogProps) => {
  const layerStyle: CSSProperties = { zIndex };

  // Ark can leave `pointer-events: none` on <body> if the dialog unmounts
  // without a full close lifecycle (e.g. parent clears data on close).
  useEffect(() => {
    if (open !== false) return;
    const id = window.setTimeout(() => {
      clearStaleBodyOverlayStyles();
    }, 250);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    return () => {
      window.setTimeout(() => {
        clearStaleBodyOverlayStyles();
      }, 250);
    };
  }, []);

  const dialogTree = (
    <>
      <Dialog.Backdrop
        className={cx(backdropClass, layerClass)}
        style={layerStyle}
      />
      <Dialog.Positioner
        className={cx(
          positionerClass,
          placementPositionerClass[placement],
          layerClass,
        )}
        style={layerStyle}
      >
        <Dialog.Content
          className={cx(contentBaseClass, placementContentClass[placement])}
        >
          {!hideCloseTrigger ? (
            <Dialog.CloseTrigger asChild>
              <styled.button
                type="button"
                className={cx(
                  closeTriggerBaseClass,
                  closeTriggerVisualClass[closeTriggerVisual],
                )}
                aria-label="Close"
              >
                <Icon Element={Xmark} size="lg" />
              </styled.button>
            </Dialog.CloseTrigger>
          ) : null}
          {children}
        </Dialog.Content>
      </Dialog.Positioner>
    </>
  );

  return (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
      lazyMount
      unmountOnExit
      preventScroll
      closeOnEscape={false}
      closeOnInteractOutside={false}
    >
      {portal ? <Portal>{dialogTree}</Portal> : dialogTree}
    </Dialog.Root>
  );
};
